'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and Skin Absorption: What Can Actually Cross the Skin?"
      author="MicroPlastics Editorial Team"
      date="May 21, 2026"
      readTime="8 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics and skin absorption, what crosses the skin barrier"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Intact skin is a strong barrier against <em>particles</em>: most
        microplastic-sized fragments (1–5,000 µm) cannot pass through the
        stratum corneum. However, the <strong>chemicals carried by
        microplastics</strong> (BPA, BPS, phthalates, PFAS) absorb through
        skin readily. Thermal receipts, lotions and cosmetics with PEG/PVP
        polymers, and synthetic clothing all transfer chemicals
        transdermally. Nanoplastics (&lt;1 µm) may also cross the skin barrier
        under certain conditions (damaged skin, hair follicles, sweat glands).
        For practical purposes: focus on what you put on your skin, not on
        whether you touch plastic.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Healthy intact skin blocks particles &gt;1 µm, most microplastics cannot cross.</>,
          <>The plastic chemicals (BPA, phthalates, PFAS) absorb through skin readily and are detectable in blood within hours.</>,
          <>Thermal paper receipts are coated with BPA/BPS that absorbs through fingertip skin.</>,
          <>Cosmetics and lotions containing PEG, polyethylene, or acrylates copolymer apply microplastic polymers directly.</>,
          <>Damaged skin (sunburn, cuts, dermatitis), hair follicles, and sweat glands offer absorption shortcuts for both particles and chemicals.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why intact skin blocks most particles
      </h2>
      <p>
        The stratum corneum, the outermost layer of dead skin cells held
        together by lipids, is an effective barrier against particles larger
        than about 1 micrometre. Most microplastic particles in the environment
        are 5–500 µm, well outside the skin&apos;s permeability range.
        Laboratory studies on isolated skin samples consistently show negligible
        particle penetration at typical environmental exposures.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        But the chemicals absorb readily
      </h2>
      <p>
        Plastic-associated chemicals are small enough to cross the skin barrier, many are deliberately formulated to penetrate (in pharmaceutical and
        cosmetic contexts). Several human-relevant examples:
      </p>

      <BlogComparisonTable
        caption="Plastic chemicals known to absorb through human skin"
        columns={[
          { key: 'chemical', label: 'Chemical' },
          { key: 'source', label: 'Common skin-contact source' },
          { key: 'evidence', label: 'Skin-absorption evidence' },
        ]}
        rows={[
          { chemical: 'BPA / BPS', source: 'Thermal receipts, polycarbonate water bottles', evidence: 'Detected in blood within 30-60 minutes of handling receipts (Hormann et al. 2014)' },
          { chemical: 'Phthalates (DEHP, DBP)', source: 'PVC products, vinyl gloves, scented lotions', evidence: 'Metabolites appear in urine after dermal exposure (CDC NHANES)' },
          { chemical: 'PFAS', source: 'Water-resistant clothing, cosmetics with "fluoro" ingredients', evidence: 'Cross skin slowly but cumulatively in chronic exposure' },
          { chemical: 'Triclosan', source: 'Antibacterial soaps (now restricted)', evidence: 'Documented dermal absorption' },
          { chemical: 'Parabens', source: 'Cosmetics, deodorants, lotions', evidence: 'Estrogenic activity from skin exposure documented' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Three skin-routes worth knowing
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Thermal receipts.</strong> Hormann et al. (2014, <em>PLOS One</em>) showed handling a thermal receipt for 5 seconds and then handling food (e.g. at a checkout) increased BPA exposure measurably. People handling receipts daily as part of their job (cashiers, gas station attendants) have substantially higher urinary BPA than average. Hand sanitiser multiplies the dose; the{' '}
          <Link href="/blog/bpa-on-thermal-receipts" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
            thermal receipt guide
          </Link>{' '}
          explains why, and what &ldquo;BPA-free&rdquo; paper uses instead.
        </li>
        <li>
          <strong>Cosmetics and lotions with synthetic polymers.</strong>
          Anything you apply to skin and leave on (moisturisers, sunscreens, lip products, deodorants) that lists PEG, polyethylene, acrylates copolymer, carbomer, or polyquaternium-(any number) is depositing polymer onto your skin. Particles may not cross, but the chemistry does.
        </li>
        <li>
          <strong>Synthetic textiles.</strong> Polyester, nylon, and acrylic clothing release brominated flame retardants and plasticisers that absorb through skin during contact, especially during sweat-generating activities.
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical skin-protective changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Decline thermal receipts</strong> at gas stations and retail
          checkouts when possible. Ask for email receipts.
        </li>
        <li>
          <strong>Wash hands after handling receipts</strong> before eating.
          Especially important for daily cashier-like exposure.
        </li>
        <li>
          <strong>Read leave-on cosmetic labels</strong> for PEG, polyethylene,
          acrylates copolymer, carbomer. Brands certified by EWG VERIFIED,
          MADE SAFE, or COSMOS Organic typically avoid synthetic polymers.
        </li>
        <li>
          <strong>Switch to natural-fibre clothing</strong>, cotton, linen,
          wool, silk, hemp, Tencel. Especially underwear and sleepwear (8+
          hours of contact daily).
        </li>
        <li>
          <strong>Avoid &ldquo;fragrance&rdquo; in unspecified products.</strong>
          Often a phthalate-carrying mixture.
        </li>
        <li>
          <strong>Choose mineral sunscreens</strong> (zinc oxide, titanium
          dioxide) over chemical sunscreens that may contain synthetic
          polymers and absorb-able UV filters.
        </li>
        <li>
          <strong>Limit hot-tub and pool exposure</strong> with synthetic
          swimsuits, heat + chlorine + sweat increases textile chemical
          migration into skin.
        </li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-cosmetics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in cosmetics
        </Link>,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in toothpaste and personal care
        </Link>, and{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          best polymer-free toothpaste
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Ingredient list parsed from the product label or barcode.</>,
          <>Flagged ingredients, polyethylene, acrylates, carbomer, PEG, fragrance, parabens.</>,
          <>Product category, leave-on vs rinse-off; risk weighted differently.</>,
          <>Brand and product line, clean certifications (EWG VERIFIED, MADE SAFE).</>,
          <>Cited research and regulatory references for each scan.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan personal-care products before buying"
        sub="The MicroPlastics app reads the ingredient list, flags microplastic polymers and additives, and points to cleaner alternatives in the same category."
        buttonLabel="Scan cosmetics in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do microplastics absorb through the skin?',
            answer:
              'Most microplastic particles (1-500 µm) are too large to cross intact skin. However, nanoplastics (<1 µm) may penetrate under some conditions, and the chemicals carried by microplastics (BPA, phthalates, PFAS) absorb through skin readily.',
          },
          {
            question: 'Are thermal receipts dangerous?',
            answer:
              'Thermal paper receipts are coated with BPA or BPS, which absorbs through skin. Hormann et al. 2014 showed measurable blood BPA increases after just 5 seconds of receipt contact. Cashiers and others with daily receipt handling have substantially higher urinary BPA.',
          },
          {
            question: 'Can microplastics in lotions or cosmetics be absorbed?',
            answer:
              'The particles themselves mostly stay on the skin surface. However, lotions and cosmetics that contain PEG, polyethylene, polyquaternium, or acrylates copolymer apply synthetic polymers directly to skin, where chemical components can absorb. Many also contain phthalates as "fragrance".',
          },
          {
            question: 'Do clothes contribute to microplastic exposure through skin?',
            answer:
              'Synthetic fabrics (polyester, nylon, acrylic) release plasticisers and brominated flame retardants that absorb through skin during prolonged contact, especially with sweating. Underwear, sleepwear, and activewear in natural fibres reduce this exposure.',
          },
          {
            question: 'Are mineral sunscreens better for microplastic exposure?',
            answer:
              'Mineral sunscreens (zinc oxide, titanium dioxide) typically have simpler ingredient lists with fewer synthetic polymers than chemical sunscreens. Look for products certified by EWG VERIFIED or MADE SAFE for the most rigorous ingredient screening.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Hormann AM, vom Saal FS, Nagel SC, et al.',
            year: 2014,
            title: 'Holding thermal receipt paper and eating food after using hand sanitizer results in high serum bioactive and urine total levels of bisphenol A (BPA)',
            publisher: 'PLOS One',
            url: 'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0110509',
          },
          {
            authors: 'Cox KD, Covernton GA, Davies HL, et al.',
            year: 2019,
            title: 'Human consumption of microplastics',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b01517',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2023,
            title: 'Re-evaluation of bisphenol A (BPA)',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/6857',
          },
          {
            authors: 'Centers for Disease Control and Prevention',
            year: 2022,
            title: 'Fourth National Report on Human Exposure to Environmental Chemicals (Updated Tables)',
            publisher: 'CDC',
            url: 'https://www.cdc.gov/exposurereport/index.html',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
